import React from 'react';
import PropTypes from 'prop-types';
import FadeIn from 'react-fade-in';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Preloader from 'src/components/Preloader';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MuiTable from '@mui/material/Table';
import MuiTableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import SkeletonBlock from 'src/components/SkeletonBlock';
import TableCell from './TableCell';

const SkeletonRows = ({ rows, columns }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      // eslint-disable-next-line react/no-array-index-key
      <MuiTableRow key={`skeleton-row-${rowIndex}`}>
        {Array.from({ length: columns }).map((__, columnIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableCell key={`skeleton-cell-${rowIndex}-${columnIndex}`}>
            <SkeletonBlock />
          </TableCell>
        ))}
      </MuiTableRow>
    ))}
  </>
);

SkeletonRows.propTypes = {
  rows: PropTypes.number.isRequired,
  columns: PropTypes.number.isRequired
};

const Table = ({
  className,
  theads: Theads,
  tbody: Tbody,
  pagination: Pagination,
  loading,
  customMaxHeight,
  tableSx,
  skeletonLoading,
  skeletonColumns,
  skeletonRows,
  ...rest
}) => {
  const showSkeletonRows = loading && skeletonLoading;
  const showPreloader = loading && !skeletonLoading;

  return (
    <Card className={className} {...rest}>
      <PerfectScrollbar>
        <Box>
          <FadeIn transitionDuration={200}>
            {showPreloader ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  maxHeight: customMaxHeight || '70vh'
                }}
              >
                <Preloader style={{ height: '68vh' }} />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: customMaxHeight || '70vh'
                }}
              >
                <MuiTable stickyHeader sx={tableSx}>
                  <TableHead>
                    <Theads />
                  </TableHead>
                  <TableBody>
                    {showSkeletonRows ? (
                      <SkeletonRows
                        rows={skeletonRows}
                        columns={skeletonColumns}
                      />
                    ) : (
                      <Tbody />
                    )}
                  </TableBody>
                </MuiTable>
              </TableContainer>
            )}

            {Pagination ? <Pagination /> : null}
          </FadeIn>
        </Box>
      </PerfectScrollbar>
    </Card>
  );
};

Table.propTypes = {
  className: PropTypes.string,
  theads: PropTypes.func.isRequired,
  tbody: PropTypes.func.isRequired,
  pagination: PropTypes.func,
  loading: PropTypes.bool,
  customMaxHeight: PropTypes.string,
  tableSx: PropTypes.object,
  skeletonLoading: PropTypes.bool,
  skeletonColumns: PropTypes.number,
  skeletonRows: PropTypes.number
};

Table.defaultProps = {
  skeletonLoading: false,
  skeletonColumns: 1,
  skeletonRows: 5
};

export default Table;
